import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { PointerEvent, ReactNode } from 'react'

type Side = 'left' | 'right'
type Widths = Record<Side, number>
const DEFAULT_WIDTHS: Widths = { left: 390, right: 360 }
const MIN_WIDTH = 280
const MAX_WIDTH = 720
const DIVIDER_WIDTH = 6
const COLLAPSED_WIDTH = 40

function readWidths(key: string): Widths {
  try {
    const saved = JSON.parse(localStorage.getItem(key) || 'null')
    const width = (side: Side) => typeof saved?.[side] === 'number' && Number.isFinite(saved[side])
      ? Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, saved[side])) : DEFAULT_WIDTHS[side]
    return { left: width('left'), right: width('right') }
  } catch {
    return DEFAULT_WIDTHS
  }
}

export function ScreenerWorkspace({ storageKey, leftOpen, rightOpen, left, right, children, widths: controlledWidths, onWidthsChange }: {
  storageKey: string
  leftOpen: boolean
  rightOpen: boolean
  left: ReactNode
  right: ReactNode
  children: ReactNode
  widths?: Widths
  onWidthsChange?: (widths: Widths) => void
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [widths, setWidths] = useState(() => controlledWidths || readWidths(storageKey))
  const widthsRef = useRef(widths)
  const [containerWidth, setContainerWidth] = useState(0)
  const dragRef = useRef<{ side: Side; startX: number; startWidth: number; max: number } | null>(null)
  const frameRef = useRef<number | null>(null)
  const restoreRef = useRef<(() => void) | null>(null)

  useLayoutEffect(() => {
    const next = controlledWidths || readWidths(storageKey)
    widthsRef.current = next
    setWidths(next)
  }, [storageKey, controlledWidths])

  useLayoutEffect(() => {
    const container = containerRef.current
    if (!container) return
    const measure = () => setContainerWidth(container.clientWidth)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(container)
    return () => observer.disconnect()
  }, [])

  useEffect(() => () => {
    if (frameRef.current !== null) cancelAnimationFrame(frameRef.current)
    restoreRef.current?.()
  }, [])

  // Reserve room for the charts, including when the browser itself gets narrower.
  const chartMinimum = Math.min(360, containerWidth * 0.45)
  const panelBudget = Math.max(0, containerWidth - chartMinimum
    - (leftOpen ? DIVIDER_WIDTH : COLLAPSED_WIDTH) - (rightOpen ? DIVIDER_WIDTH : 0))
  const requested = (leftOpen ? widths.left : 0) + (rightOpen ? widths.right : 0)
  const scale = containerWidth > 0 && requested > panelBudget ? panelBudget / requested : 1
  const visibleWidths = {
    left: leftOpen ? Math.round(widths.left * scale) : COLLAPSED_WIDTH,
    right: rightOpen ? Math.round(widths.right * scale) : 0,
  }
  const maxWidth = (side: Side) => Math.max(0, Math.min(MAX_WIDTH, panelBudget
    - (side === 'left' ? visibleWidths.right : leftOpen ? visibleWidths.left : 0)))

  const persist = () => {
    // Preserve the proportions of temporarily constrained panels across reloads,
    // while retaining useful minimum preferences for a wider viewport.
    const factor = Math.max(1,
      leftOpen && widthsRef.current.left > 0 ? MIN_WIDTH / widthsRef.current.left : 1,
      rightOpen && widthsRef.current.right > 0 ? MIN_WIDTH / widthsRef.current.right : 1)
    const saved = {
      left: leftOpen ? widthsRef.current.left * factor : widthsRef.current.left,
      right: rightOpen ? widthsRef.current.right * factor : widthsRef.current.right,
    }
    widthsRef.current = saved
    setWidths(saved)
    if (onWidthsChange) {
      onWidthsChange({
        left: Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, saved.left)),
        right: Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, saved.right)),
      })
      return
    }
    try { localStorage.setItem(storageKey, JSON.stringify(saved)) } catch { /* Optional preference. */ }
  }
  const updateWidth = (side: Side, value: number, max: number) => {
    const next = {
      left: leftOpen ? visibleWidths.left : widthsRef.current.left,
      right: rightOpen ? visibleWidths.right : widthsRef.current.right,
      [side]: Math.round(Math.max(Math.min(MIN_WIDTH, max), Math.min(max, value))),
    }
    widthsRef.current = next
    setWidths(next)
  }
  const finishDrag = () => {
    if (!dragRef.current) return
    dragRef.current = null
    restoreRef.current?.()
    restoreRef.current = null
    persist()
  }
  const startDrag = (side: Side, event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return
    event.preventDefault()
    event.currentTarget.setPointerCapture(event.pointerId)
    widthsRef.current = {
      left: leftOpen ? visibleWidths.left : widthsRef.current.left,
      right: rightOpen ? visibleWidths.right : widthsRef.current.right,
    }
    dragRef.current = { side, startX: event.clientX, startWidth: visibleWidths[side], max: maxWidth(side) }
    const { cursor, userSelect } = document.body.style
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    restoreRef.current = () => { document.body.style.cursor = cursor; document.body.style.userSelect = userSelect }
  }
  const moveDrag = (event: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current
    if (!drag) return
    const delta = (event.clientX - drag.startX) * (drag.side === 'left' ? 1 : -1)
    // Keep the last pointer position, but render at most once per browser frame.
    widthsRef.current = { ...widthsRef.current, [drag.side]: Math.round(Math.max(Math.min(MIN_WIDTH, drag.max), Math.min(drag.max, drag.startWidth + delta))) }
    if (frameRef.current === null) {
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = null
        setWidths(widthsRef.current)
      })
    }
  }
  const divider = (side: Side) => (
    <div
      role="separator"
      aria-label={`Resize ${side === 'left' ? 'ticker' : 'orderbook'} panel`}
      aria-orientation="vertical"
      aria-valuemin={Math.round(Math.min(MIN_WIDTH, maxWidth(side)))}
      aria-valuemax={Math.round(maxWidth(side))}
      aria-valuenow={visibleWidths[side]}
      tabIndex={0}
      title="Drag to resize · Double-click to reset"
      className="group relative w-1.5 shrink-0 cursor-col-resize touch-none bg-border/20 outline-none hover:bg-teal-500/30 focus-visible:bg-teal-500/40"
      onPointerDown={(event) => startDrag(side, event)}
      onPointerMove={moveDrag}
      onPointerUp={finishDrag}
      onPointerCancel={finishDrag}
      onLostPointerCapture={finishDrag}
      onDoubleClick={() => { updateWidth(side, DEFAULT_WIDTHS[side], maxWidth(side)); persist() }}
      onKeyDown={(event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return
        event.preventDefault()
        const delta = (event.key === 'ArrowRight' ? 1 : -1) * (side === 'left' ? 1 : -1) * (event.shiftKey ? 50 : 10)
        const value = event.key === 'Home' ? MIN_WIDTH : event.key === 'End' ? maxWidth(side) : visibleWidths[side] + delta
        updateWidth(side, value, maxWidth(side))
        persist()
      }}
    >
      <span className="absolute inset-y-0 left-1/2 w-px bg-border/40 group-hover:bg-teal-400/70" />
    </div>
  )

  return (
    <div ref={containerRef} className="flex min-h-0 min-w-0 flex-1 overflow-hidden">
      <div className="flex min-w-0 shrink-0 overflow-hidden" style={{ width: visibleWidths.left }}>{left}</div>
      {leftOpen && divider('left')}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">{children}</div>
      {rightOpen && divider('right')}
      {rightOpen && <div className="flex min-w-0 shrink-0 overflow-hidden" style={{ width: visibleWidths.right }}>{right}</div>}
    </div>
  )
}
